/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useEffect } from 'react'
import { Box, CircularProgress, useTheme } from '@mui/material'
import { FormProvider, useForm, useFieldArray, type SubmitHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import DefaultModalActions from '../../../components/GenericModal/DefaultModalActions'
import { ButtonType } from '../../../components/GenericButton/styles'
import { useMutation } from '@tanstack/react-query'
import { notification } from '../../../components/Notifications'
import { SlotStatus, type ISlot } from '../../../models/DistributionPlan'
import { AutoImportManager } from '../../../APIManager'
import { type ISurveyDates } from '../../../models/AutoImports'
import FormSection from './FormFields'
import { defaultAllSurveyDates, surveyDatesSchema } from './constant'

export interface FormTypes {
  sectionFieldType: string
  condition: string
  noOfDays: string | number
  enabled: boolean
  surveyDateType: string
  subTitle: string
}

interface FormValue {
  surveyDatesFilters: FormTypes[]

}

interface SurveyDatesPanelProps {
  handleCloseModal: () => void
  setNextTab: () => void
  slot: ISlot
  loading: boolean
  surveyDatesList: ISurveyDates[]
}

const SurveyDatesPanel: React.FC<SurveyDatesPanelProps> = ({ handleCloseModal, slot, setNextTab, loading, surveyDatesList }): JSX.Element => {
  const isRollingResultsEnabled = slot.isRollingResultsEnabled
  const methods = useForm<any>({
    resolver: yupResolver(surveyDatesSchema),
    defaultValues: {
      surveyDatesFilters: defaultAllSurveyDates
    }
  })
  const theme = useTheme()
  const { handleSubmit, control, setValue, formState: { isDirty } } = methods
  const { fields } = useFieldArray({
    control,
    name: 'surveyDatesFilters'
  })
  const { mutate, isPending } = useMutation({
    mutationFn: AutoImportManager.submitSurveyDates,
    onSuccess: (res) => {
      notification(res?.message, { variant: 'success' })
      setNextTab()
    },
    onError: (err: any) => {
      notification(err?.error ?? 'Internal server error', { variant: 'error' })
    }
  })

  const onSubmit: SubmitHandler<FormValue> = async (values) => {
    if (!isDirty) {
      setNextTab()
      return
    }
    const payload: Array<Partial<FormTypes>> = []

    values.surveyDatesFilters.forEach((value: FormTypes) => {
      const noOfDays = value.noOfDays || 0
      const preparedNoOfDays = value.condition === 'before' ? -noOfDays : noOfDays
      if (value.enabled) {
        payload.push({
          noOfDays: Number(preparedNoOfDays),
          sectionFieldType: value.sectionFieldType,
          surveyDateType: value.surveyDateType
        })
      }
    })
    const data = { payload, slotId: slot.slotId }
    mutate(data)
  }

  useEffect(() => {
    if (surveyDatesList?.length === 0) {
      return
    }

    const mappedData = surveyDatesList?.map((d: ISurveyDates) => {
      const noOfDays = Math.abs(d.noOfDays) // Remove negative sign from noOfDays

      let condition: string | number = 'on'
      if (d.noOfDays > 0) {
        condition = 'after'
      } else if (d.noOfDays < 0) {
        condition = 'before'
      }

      let subTitle = ''

      // Dynamically set the subTitle based on surveyDateType
      switch (d.surveyDateType) {
        case 'Course Section Survey Start Date':
          subTitle = 'The day the survey for each course section begins.'
          break
        case 'Course Section Survey End Date':
          subTitle = 'The day the survey for each course section ends.'
          break
        case 'Course Section Admin Release Date':
          subTitle = 'The default day survey results are released to admins for each course section.'
          break
        case 'Course Section Instructor Release Date':
          subTitle = 'The default day survey results are released to instructors for each course section.'
          break
        default:
          subTitle = 'No subtitle available'
          break
      }

      return {
        enabled: d.enabled,
        noOfDays,
        condition,
        sectionFieldType: d.sectionFieldType,
        surveyDateType: d.surveyDateType,
        subTitle
      }
    })

    const mergedData = defaultAllSurveyDates.map((defaultItem) => {
      const itemFromBackend = mappedData?.find(d => d.surveyDateType === defaultItem.surveyDateType)
      return itemFromBackend ?? defaultItem
    })

    setValue('surveyDatesFilters', mergedData ?? [])
  }, [surveyDatesList, setValue])
  const slotStatus = slot?.slotNodeDetails?.slotStatus
  const isButtonDisabled = slotStatus !== SlotStatus.Draft
  return (
    <FormProvider {...methods}>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box minHeight={theme.spacing(112.5)}>
          {
            loading
              ? <Box display="flex" justifyContent="center" alignItems="center" minHeight={theme.spacing(100)} >
                <CircularProgress />
              </Box>
              : fields.map((item: Record<'id', string>, index: number) =>
                (isRollingResultsEnabled && (index === 2 || index === 3))
                  ? null
                  : (
                    <FormSection
                      fields={fields}
                      key={item.id}
                      index={index}
                      control={control}
                      disabled={isButtonDisabled}
                    />
                    )
              )}

        </Box>

        <DefaultModalActions
          confirmButtonProps={{
            type: 'submit',
            text: 'Next',
            buttonType: ButtonType.Primary,
            disabled: isPending || isButtonDisabled,
            loading: isPending
          }}
          destructiveButtonProps={{
            onClick: handleCloseModal,
            disabled: isPending
          }}
        />
      </form>
    </FormProvider>
  )
}

export default SurveyDatesPanel
